/**
 * Per-tab, in-memory "notify me when this turn finishes" registry.
 *
 * Replaces the old OS/browser Notification permission flow (previously
 * src/hooks/use-response-notifications.ts, now removed) as the mechanism
 * behind the "Turn on response notifications" prompt in chat-view.tsx.
 * Clicking that prompt now arms this chat's *current* turn instead of
 * requesting OS permission — the app-shell listener
 * (use-global-response-notifications.ts) checks this registry before
 * showing its in-app toast for a chat the user has navigated away from.
 *
 * Deliberately not persisted anywhere (DB, localStorage): the ask is
 * per-turn by design (see response-notification-prompt.tsx's doc comment) —
 * a reload or a fresh tab is supposed to ask again, not silently remember a
 * prior "yes."
 */

type ChatKind = "chat" | "project";

const armed = new Set<string>();

function key(id: string, kind: ChatKind): string {
  return `${kind}:${id}`;
}

export function armNotifyOnFinish(id: string, kind: ChatKind): void {
  armed.add(key(id, kind));
}

/** Clears the arm state, whether or not it was ever set — call this once a
 * turn for this chat has been resolved (notified, seen live, or otherwise
 * accounted for) so a stale arm never leaks into the *next* turn. */
export function disarmNotifyOnFinish(id: string, kind: ChatKind): void {
  armed.delete(key(id, kind));
}

export function isNotifyOnFinishArmed(id: string, kind: ChatKind): boolean {
  return armed.has(key(id, kind));
}
